import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentWallet from './DocumentWallet';
import api from '../utils/api';
import Badge from './Badge';
import StateBlock from './StateBlock';

const TABS = ['Overview', 'Work Orders', 'AR Invoices', 'Ledger', 'Documents'];

export default function ClientPanel({ clientId, onClose, onEdit }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/clients/${clientId}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [clientId]);

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel">
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title">{data?.name || 'Loading…'}</h3>
            {data && <div className="form-hint">{data.contact_name || 'No primary contact on file'}</div>}
          </div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        {loading && <StateBlock type="loading" />}

        {data && (
          <>
            <div className="panel-tabs">
              {TABS.map(t => (
                <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
                  {t}
                </button>
              ))}
            </div>

            <div className="panel-body">
              <div className="panel-summary-line">
                <Badge variant={data.is_active ? 'success' : 'neutral'}>{data.is_active ? 'Active' : 'Inactive'}</Badge>
                {' · '}
                Net {data.net_terms} · {data.work_orders.length} work order{data.work_orders.length !== 1 ? 's' : ''}
              </div>

              {tab === 'Overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                  <Field label="Contact" value={data.contact_name} />
                  <Field label="Email" value={data.email} />
                  <Field label="Phone" value={data.phone} />
                  <Field label="Address" value={data.address} />
                  <Field label="Tax ID" value={data.tax_id} />
                  <Field label="Net terms" value={data.net_terms ? `Net ${data.net_terms}` : null} />
                  <Field label="Notes" value={data.notes} />
                </div>
              )}

              {tab === 'Work Orders' && (
                data.work_orders.length === 0
                  ? <StateBlock type="empty" title="No work orders yet" />
                  : data.work_orders.map(wo => (
                    <div key={wo.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600 }}>{wo.client_wo_number}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {wo.engagement_type.replace('_', ' ')} · {wo.first_name} {wo.last_name} · <Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge>
                      </div>
                    </div>
                  ))
              )}

              {tab === 'AR Invoices' && (
                <>
                  <SummaryCards
                    cards={[
                      { label: 'Total Billed', value: data.summary.total_billed },
                      { label: 'Verified Received', value: data.summary.total_verified_received },
                      { label: 'Outstanding', value: data.summary.outstanding },
                    ]}
                  />
                  {data.ar_invoices.slice(0, 6).map(inv => (
                    <InvoiceRow key={inv.id} inv={inv} />
                  ))}
                </>
              )}

              {tab === 'Ledger' && <ClientLedgerSummary clientId={data.id} />}

              {tab === 'Documents' && <DocumentWallet entityType="client" entityId={data.id} entityName={data.name} />}
            </div>

            <div className="panel-footer" style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => onEdit(data)}>Edit Client</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => navigate(`/clients/${clientId}`)}>View Full Profile →</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function SummaryCards({ cards }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.label}</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>${(c.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
      ))}
    </div>
  );
}

function InvoiceRow({ inv }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span>{inv.invoice_number}</span>
      <span>${(inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
  );
}

function ClientLedgerSummary({ clientId }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    api.get(`/clients/${clientId}/ledger`).then(r => setLedger(r.data));
  }, [clientId]);

  if (!ledger) return <StateBlock type="loading" />;
  if (ledger.rows.length === 0) return <StateBlock type="empty" title="No ledger activity yet" />;

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ledger Balance</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(ledger.summary.ledger_balance)}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Balance</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>{fmt(ledger.summary.verified_balance)}</div>
        </div>
      </div>
      {ledger.rows.slice(-5).reverse().map((r, i) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{r.invoice_number}</span>
            <span>{fmt(r.amount)}</span>
          </div>
          {r.payment_date && (
            <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>Paid {r.payment_date}</span>
              <Badge variant={r.verification_status === 'verified' ? 'success' : 'warning'}>{r.verification_status}</Badge>
            </div>
          )}
        </div>
      ))}
      <a href="/ledgers" style={{ display: 'block', textAlign: 'center', marginTop: 14, fontSize: 13 }}>
        Open full Ledgers page for complete history & export →
      </a>
    </div>
  );
}
